import { api } from '@/lib/axios';

export interface CharStats {
  ba_wins: number;
  ctf_wins: number;
  rleague_wins: number;
  survival_wins: number;
  fortress_wins: number;
  lms_wins: number;
  total_pvp_kills: number;
  total_unique_kills: number;
  total_globals_sent: number;
}

export interface EventLogRow {
  event_type?: string;
  event_subtype?: string;
  target_name?: string;
  event_time?: string;
  event_time_ago?: string;
  details?: string;
}

export interface PreloadedSideData {
  stats: { success?: boolean; data?: Record<string, unknown> };
  logs: { success?: boolean; data?: EventLogRow[] };
  buffs: { success?: boolean; data?: { buffs: unknown[]; runes: unknown[] } };
}

const EMPTY_STATS: CharStats = {
  ba_wins: 0,
  ctf_wins: 0,
  rleague_wins: 0,
  survival_wins: 0,
  fortress_wins: 0,
  lms_wins: 0,
  total_pvp_kills: 0,
  total_unique_kills: 0,
  total_globals_sent: 0,
};

export function normalizeCharStats(raw: Record<string, unknown> | undefined): CharStats {
  if (!raw) return EMPTY_STATS;
  const num = (key: keyof CharStats) =>
    Number(raw[key] ?? raw[key.toUpperCase()] ?? raw[String(key).toUpperCase()] ?? 0);
  return {
    ba_wins: num('ba_wins'),
    ctf_wins: num('ctf_wins'),
    rleague_wins: num('rleague_wins'),
    survival_wins: num('survival_wins'),
    fortress_wins: num('fortress_wins'),
    lms_wins: num('lms_wins'),
    total_pvp_kills: num('total_pvp_kills'),
    total_unique_kills: num('total_unique_kills'),
    total_globals_sent: num('total_globals_sent'),
  };
}

export function timeAgo(dateString?: string): string {
  if (!dateString) return 'just now';
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 0 || Number.isNaN(seconds)) return 'just now';
  if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

export function activityTimeLabel(log: EventLogRow): string {
  if (log.event_time_ago) return log.event_time_ago;
  return timeAgo(log.event_time);
}

export function globalMessage(log: EventLogRow): string {
  try {
    if (log.details) {
      const details = JSON.parse(log.details) as { message?: string };
      return details.message || 'Global message';
    }
  } catch {
    if (log.details) return log.details;
  }
  return 'Global message';
}

export function activityItem(log: EventLogRow): { icon: string; text: string; className: string } {
  switch (log.event_type) {
    case 'event_win':
      return { icon: '🏆', text: `Won ${log.event_subtype ?? 'event'} event`, className: 'activity-win' };
    case 'pvp_kill':
      return {
        icon: '⚔️',
        text: `Killed ${log.target_name || 'Unknown'} in ${log.event_subtype ?? ''}`,
        className: 'activity-kill',
      };
    case 'unique_kill': {
      const location = log.event_subtype?.trim();
      const where = location ? ` in ${location}` : '';
      return {
        icon: '👹',
        text: `Defeated ${log.target_name ?? 'Unknown'}${where}`,
        className: 'activity-unique',
      };
    }
    case 'global':
      return { icon: '📢', text: `"${globalMessage(log)}"`, className: 'activity-global' };
    default:
      return { icon: '📝', text: 'Unknown event', className: '' };
  }
}

function normalizeBuffsSidePayload(
  buffsRes: PromiseSettledResult<{ data: { success?: boolean; data?: { buffs?: unknown[]; runes?: unknown[] } } }>,
): PreloadedSideData['buffs'] {
  if (buffsRes.status !== 'fulfilled') {
    return { success: true, data: { buffs: [], runes: [] } };
  }

  const body = buffsRes.value.data;
  if (body?.data && Array.isArray(body.data.buffs) && Array.isArray(body.data.runes)) {
    return { success: true, data: { buffs: body.data.buffs, runes: body.data.runes } };
  }
  if (body?.success && body.data) {
    return body as PreloadedSideData['buffs'];
  }

  return { success: true, data: { buffs: [], runes: [] } };
}

export async function preloadCharacterSideData(charId: number): Promise<PreloadedSideData> {
  const [statsRes, logsRes, buffsRes] = await Promise.allSettled([
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/api/v1/character/${charId}/stats`),
    api.get<{ success: boolean; data: EventLogRow[] }>(`/api/v1/character/${charId}/event-log`, {
      params: { page: 1, limit: 10 },
    }),
    api.get<{ success: boolean; data: { buffs: unknown[]; runes: unknown[] } }>(
      `/api/v1/character/${charId}/buffs`,
    ),
  ]);

  return {
    stats:
      statsRes.status === 'fulfilled'
        ? statsRes.value.data
        : { success: true, data: {} },
    logs:
      logsRes.status === 'fulfilled'
        ? logsRes.value.data
        : { success: true, data: [] },
    buffs: normalizeBuffsSidePayload(buffsRes),
  };
}

/** Precarga para tab buffs de itemTooltip (sin modificar itemTooltip.js) */
export function applyCharacterSidePreload(charId: number, side: PreloadedSideData): void {
  window.__PRELOADED_CHARACTER_STATS__ = { charId, stats: side.stats, logs: side.logs };
  window.__PRELOADED_CHARACTER_BUFFS__ = { charId, payload: side.buffs };
}

declare global {
  interface Window {
    __PRELOADED_CHARACTER_STATS__?: { charId: number; stats: unknown; logs: unknown };
    __PRELOADED_CHARACTER_BUFFS__?: { charId: number; payload: unknown };
  }
}
